import {
  ArrowPathIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  CodeBracketIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import UserContext from "context/UserContext";

import Card from "components/atoms/Card";
import Spinner from "components/atoms/Spinner";

import { error } from "utils";

type AggregateCount = {
  key: string;
  count: number;
};

type CurrencyAggregate = {
  currency: string;
  count: number;
  saleAmount: number;
  payoutAmount: number;
};

type AffiliateSummaryResponse = {
  trackedLinks: number;
  clicks: number;
  conversions: number;
  pendingConversions: number;
  approvedConversions: number;
  lockedConversions: number;
  eligibleConversions: number;
  reversedConversions: number;
  totalSaleAmount: number;
  totalPayoutAmount: number;
  eligiblePayoutAmount: number;
  lastConversionAt?: string;
  lastTrackedLinkAt?: string;
  lastClickAt?: string;
  topSourceTools: AggregateCount[];
  topCampaigns: AggregateCount[];
  topCurrencies: CurrencyAggregate[];
};

type ConversionRow = {
  impactActionId: string;
  status: string;
  saleAmount?: number;
  payoutAmount?: number;
  currency?: string;
  impactSubId1?: string;
  impactSubId2?: string;
  impactSubId3?: string;
  actionOccurredAt?: string;
  actionLockedAt?: string;
  sourceTool?: string;
  campaign?: string;
  cartId?: string;
  trackedUrl?: string;
  destinationUrl?: string;
  isPayoutEligible: boolean;
  created: string;
  updated: string;
};

type LinkRow = {
  affiliateLinkId: string;
  sourceTool?: string;
  campaign?: string;
  cartId?: string;
  trackedUrl?: string;
  destinationUrl?: string;
  impactSubId1?: string;
  impactSubId2?: string;
  impactSubId3?: string;
  clickCount: number;
  conversionCount: number;
  totalSaleAmount: number;
  totalPayoutAmount: number;
  lastClickAt?: string;
  lastConversionAt?: string;
  created: string;
  updated: string;
};

type Pagination = {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  nextOffset?: number;
};

type ConversionListResponse = {
  rows: ConversionRow[];
  pagination: Pagination;
};

type LinkListResponse = {
  rows: LinkRow[];
  pagination: Pagination;
};

type AffiliatePayoutStatusResponse = {
  stripeCustomerId?: string;
  connectAccountId?: string;
  hasConnectAccount: boolean;
  readyForPayouts: boolean;
  payoutsEnabled: boolean;
  chargesEnabled: boolean;
  hasExternalBankAccount: boolean;
  detailsSubmitted: boolean;
  requirementsCurrentlyDue?: string[];
  requirementsPendingVerification?: string[];
  requirementsEventuallyDue?: string[];
  lastPayoutAmount?: number;
  lastPayoutCurrency?: string;
  lastPayoutStatus?: string;
  lastPayoutAt?: number;
  pendingPayoutAmount?: number;
  pendingPayoutCurrency?: string;
};

type AffiliatePayoutLinkResponse = {
  url: string;
};

type AffiliatePayoutLedgerRow = {
  affiliatePayoutId: string;
  customerId: string;
  affiliateName: string;
  connectAccountId?: string;
  periodStart: string;
  periodEnd: string;
  payoutCurrency: string;
  grossSaleAmount: number;
  grossPayoutAmount: number;
  adjustmentAmount: number;
  netPayoutAmount: number;
  sourceConversionCount: number;
  payoutStatus: string;
  stripePayoutId?: string;
  notes?: string;
  createdBy?: string;
  created: string;
  updated: string;
};

type AffiliatePayoutLedgerListResponse = {
  rows: AffiliatePayoutLedgerRow[];
  pagination: Pagination;
};

type CustomerOption = {
  id: string;
  name: string;
  ownerEmail: string;
};

type CustomerOptionsResponse = {
  customers: CustomerOption[];
  total: number;
};

type AdminAffiliateOverviewRow = {
  customerId: string;
  customerName: string;
  ownerEmail: string;
  trackedLinks: number;
  clicks: number;
  conversions: number;
  approvedConversions: number;
  pendingConversions: number;
  totalSaleAmount: number;
  totalPayoutAmount: number;
  lastConversionAt?: string;
  lastLinkAt?: string;
};

type AdminAffiliateOverviewResponse = {
  rows: AdminAffiliateOverviewRow[];
  pagination: Pagination;
};

type AdminAffiliateUnmatchedActionRow = {
  impactActionId: string;
  affiliateName?: string;
  status?: string;
  currency?: string;
  saleAmount?: number;
  payoutAmount?: number;
  impactSubId1?: string;
  impactSubId2?: string;
  impactSubId3?: string;
  passioCustomerId?: string;
  actionOccurredAt?: string;
  actionLockedAt?: string;
  mismatchReason?: string;
  created: string;
  updated: string;
};

type AdminAffiliateUnmatchedActionListResponse = {
  rows: AdminAffiliateUnmatchedActionRow[];
  pagination: Pagination;
};

type AdminSettlementCurrencyRow = {
  currency?: string;
  conversionCount?: number;
  grossPayoutAmount?: number;
  grossSaleAmount?: number;
  stripeTransferId?: string;
  ledgerPayoutId?: string;
  status: string;
  reason?: string;
};

type AdminSettlementCustomerRow = {
  customerId: string;
  connectAccountId: string;
  rows: AdminSettlementCurrencyRow[];
};

type AdminSettlementResponse = {
  processedAt: string;
  dryRun: boolean;
  customers: AdminSettlementCustomerRow[];
};

const LIMIT = 20;

const RESTRICTED_AFFILIATE_ADMIN_PATTERNS = (
  process.env.REACT_APP_ROUTE_INSIGHTS_RESTRICTED_ADMINS || "ae.branton9*"
)
  .split(",")
  .map((value) => value.trim().toLowerCase())
  .filter((value) => value.length > 0);

const emailMatchesPattern = (email: string, pattern: string): boolean => {
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedPattern = pattern.trim().toLowerCase();
  if (!normalizedEmail || !normalizedPattern) return false;
  if (normalizedPattern.endsWith("*")) {
    return normalizedEmail.startsWith(normalizedPattern.slice(0, -1));
  }
  return normalizedEmail === normalizedPattern;
};

const fmtDate = (value?: string): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const fmtMoney = (value?: number, currency = "USD"): string => {
  if (value === undefined || value === null) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 2,
  }).format(value);
};

const fmtStripeMinorMoney = (value?: number, currency = "USD"): string => {
  if (value === undefined || value === null) return "-";
  return fmtMoney(value / 100, currency);
};

const toTitle = (value?: string): string => {
  if (!value) return "Unknown";
  return value
    .replace(/[-_]/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

// Friendly labels for Stripe Connect Express requirement codes. The API returns
// raw paths like `business_profile.url` / `tos_acceptance.date` which are not
// useful to surface directly to a customer. Anything missing from this map
// falls through to a generic prettifier (last segment + capitalize words).
const STRIPE_REQUIREMENT_LABELS: Record<string, string> = {
  "business_profile.url": "Business website",
  "business_profile.mcc": "Business industry",
  "business_profile.product_description": "Product description",
  "business_profile.support_phone": "Support phone number",
  "business_profile.support_email": "Support email",
  "business_profile.name": "Business name",
  business_type: "Business type",

  "tos_acceptance.date": "Accept Stripe terms of service",
  "tos_acceptance.ip": "Accept Stripe terms of service",

  external_account: "Bank account or debit card",

  "individual.first_name": "Legal first name",
  "individual.last_name": "Legal last name",
  "individual.email": "Email address",
  "individual.phone": "Phone number",
  "individual.dob.day": "Date of birth",
  "individual.dob.month": "Date of birth",
  "individual.dob.year": "Date of birth",
  "individual.id_number": "Government ID (full SSN)",
  "individual.ssn_last_4": "Last 4 of SSN",
  "individual.address.line1": "Home address",
  "individual.address.city": "Home address",
  "individual.address.state": "Home address",
  "individual.address.postal_code": "Home address",
  "individual.address.country": "Home country",
  "individual.verification.document": "Government-issued ID document",
  "individual.verification.additional_document": "Additional ID document",
  "individual.political_exposure": "Political exposure disclosure",

  "company.name": "Company name",
  "company.tax_id": "Tax ID (EIN)",
  "company.phone": "Company phone",
  "company.address.line1": "Company address",
  "company.address.city": "Company address",
  "company.address.state": "Company address",
  "company.address.postal_code": "Company address",
  "company.address.country": "Company country",
  "company.directors_provided": "Directors information",
  "company.executives_provided": "Executives information",
  "company.owners_provided": "Owners information",
  "company.verification.document": "Company verification document",

  "relationship.representative": "Account representative",
  "relationship.owner": "Account owner",
  "relationship.director": "Director",
  "relationship.executive": "Executive",

  "settings.payouts.statement_descriptor": "Bank statement descriptor",
};

const friendlyStripeRequirement = (code: string): string => {
  const direct = STRIPE_REQUIREMENT_LABELS[code];
  if (direct) return direct;
  const last = (code.split(".").pop() || code).replace(/^_+/, "");
  return last.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const dedupeFriendlyStripeRequirements = (
  codes: string[] | undefined
): string[] => {
  if (!codes?.length) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const code of codes) {
    const label = friendlyStripeRequirement(code);
    if (!seen.has(label)) {
      seen.add(label);
      out.push(label);
    }
  }
  return out;
};

// Mirrors the env detection used elsewhere in the portal (mealPlanApiClient,
// DetailsPassioHub, DetailsNutritionPlatform). Picks the dev passiolife API
// host when running against any non-prod environment.
const isPassioDevEnv = (): boolean =>
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_ENV === "development" ||
  (typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname.includes("-dev")));

const passioApiBaseUrl = (): string =>
  isPassioDevEnv()
    ? "https://api-dev.passiolife.com"
    : "https://api.passiolife.com";

// The link generator UI deliberately only surfaces the N-API surface — that's
// the route external integrators are expected to call from their backends.
// `customerId` is omitted from inputs because the N-API token already carries
// customer context, so the server resolves it automatically.
const AFFILIATE_LINK_NAPI_PATH =
  "/v2/products/napi/affiliates/convertShoppingListToAffiliateLink";

const buildAffiliateLinkRequestUrl = (params: {
  sourceTool?: string;
  cartId?: string;
  campaign?: string;
}): string => {
  const base = `${passioApiBaseUrl()}${AFFILIATE_LINK_NAPI_PATH}`;
  const search = new URLSearchParams();
  if (params.sourceTool?.trim())
    search.set("sourceTool", params.sourceTool.trim());
  if (params.cartId?.trim()) search.set("cartId", params.cartId.trim());
  if (params.campaign?.trim()) search.set("campaign", params.campaign.trim());
  const qs = search.toString();
  return qs ? `${base}?${qs}` : base;
};

const buildAffiliateLinkCurl = (params: {
  sourceTool?: string;
  cartId?: string;
  campaign?: string;
}): string => {
  const url = buildAffiliateLinkRequestUrl(params);
  return [
    "curl --fail-with-body --silent --show-error \\",
    "  -X POST \\",
    '  -H "Authorization: Bearer ${NAPI_TOKEN}" \\',
    '  -H "Content-Type: application/json" \\',
    `  "${url}" \\`,
    "  -d '[",
    '    {"name":"Bananas","portionQuantity":6,"portionSize":"count"},',
    '    {"name":"Greek Yogurt","portionQuantity":2,"portionSize":"container"}',
    "  ]'",
  ].join("\n");
};

type CopyButtonProps = {
  value: string;
  label?: string;
  className?: string;
  iconOnly?: boolean;
};

const CopyButton = ({
  value,
  label = "Copy",
  className = "",
  iconOnly = false,
}: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(async () => {
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore clipboard errors silently; visual state just won't update
    }
  }, [value]);

  return (
    <button
      type="button"
      onClick={onCopy}
      disabled={!value}
      className={`inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      title={copied ? "Copied!" : label}
    >
      {copied ? (
        <CheckIcon className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
      )}
      {!iconOnly && <span>{copied ? "Copied" : label}</span>}
    </button>
  );
};

const AffiliateEarnings = () => {
  const userContext = useContext(UserContext);
  const [conversionSortBy, setConversionSortBy] =
    useState<string>("actionOccurredAt");
  const [conversionSortOrder, setConversionSortOrder] = useState<
    "asc" | "desc"
  >("desc");
  const [conversionOffset, setConversionOffset] = useState(0);
  const [conversionStatus, setConversionStatus] = useState("");
  const [conversionSearchInput, setConversionSearchInput] = useState("");
  const [conversionSearch, setConversionSearch] = useState("");

  const [linkSortBy, setLinkSortBy] = useState<string>("created");
  const [linkSortOrder, setLinkSortOrder] = useState<"asc" | "desc">("desc");
  const [linkOffset, setLinkOffset] = useState(0);
  const [linkSearchInput, setLinkSearchInput] = useState("");
  const [linkSearch, setLinkSearch] = useState("");
  const [payoutCtaLoading, setPayoutCtaLoading] = useState<
    "onboarding" | "dashboard" | null
  >(null);
  const [affiliateViewMode, setAffiliateViewMode] = useState<
    "customer" | "admin"
  >("customer");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customerPickerSearchInput, setCustomerPickerSearchInput] =
    useState("");
  const [adminSearchInput, setAdminSearchInput] = useState("");
  const [adminSearch, setAdminSearch] = useState("");
  const [adminSortBy, setAdminSortBy] = useState("totalPayoutAmount");
  const [adminSortOrder, setAdminSortOrder] = useState<"asc" | "desc">("desc");
  const [adminOffset, setAdminOffset] = useState(0);
  const [unmatchedSearchInput, setUnmatchedSearchInput] = useState("");
  const [unmatchedSearch, setUnmatchedSearch] = useState("");
  const [unmatchedOffset, setUnmatchedOffset] = useState(0);
  const [reconcileActionId, setReconcileActionId] = useState("");
  const [reconcileAffiliateLinkId, setReconcileAffiliateLinkId] = useState("");
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [adminSettlementLoading, setAdminSettlementLoading] = useState<
    "dryRun" | "run" | null
  >(null);
  const [adminSettlementResult, setAdminSettlementResult] =
    useState<AdminSettlementResponse | null>(null);

  const [docsExpanded, setDocsExpanded] = useState(false);
  const [builderSourceTool, setBuilderSourceTool] = useState("");
  const [builderCartId, setBuilderCartId] = useState("");
  const [builderCampaign, setBuilderCampaign] = useState("");

  const [activeAttributionFilter, setActiveAttributionFilter] = useState<{
    type: "source" | "campaign";
    value: string;
  } | null>(null);

  // Pushing the filter into both the conversion and link search inputs lets us
  // reuse the existing server-side `search` filter (which already matches
  // sourceTool / campaign / cart / subId across both tables) instead of adding
  // a parallel filter pipeline.
  const applyAttributionFilter = useCallback(
    (type: "source" | "campaign", value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      setActiveAttributionFilter({ type, value: trimmed });
      setConversionSearchInput(trimmed);
      setLinkSearchInput(trimmed);
      setConversionOffset(0);
      setLinkOffset(0);
    },
    []
  );

  const clearAttributionFilter = useCallback(() => {
    setActiveAttributionFilter(null);
    setConversionSearchInput("");
    setLinkSearchInput("");
    setConversionOffset(0);
    setLinkOffset(0);
  }, []);

  const builderUrl = useMemo(
    () =>
      buildAffiliateLinkRequestUrl({
        sourceTool: builderSourceTool,
        cartId: builderCartId,
        campaign: builderCampaign,
      }),
    [builderSourceTool, builderCartId, builderCampaign]
  );

  const builderCurl = useMemo(
    () =>
      buildAffiliateLinkCurl({
        sourceTool: builderSourceTool,
        cartId: builderCartId,
        campaign: builderCampaign,
      }),
    [builderSourceTool, builderCartId, builderCampaign]
  );

  const canViewAffiliateAdminDashboard = useMemo(() => {
    const email = (userContext?.email || "").toLowerCase();
    return RESTRICTED_AFFILIATE_ADMIN_PATTERNS.some((pattern) =>
      emailMatchesPattern(email, pattern)
    );
  }, [userContext?.email]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setConversionSearch(conversionSearchInput.trim());
      setConversionOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [conversionSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLinkSearch(linkSearchInput.trim());
      setLinkOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [linkSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAdminSearch(adminSearchInput.trim());
      setAdminOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [adminSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUnmatchedSearch(unmatchedSearchInput.trim());
      setUnmatchedOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [unmatchedSearchInput]);

  const activeCustomerOverride = selectedCustomerId || undefined;

  const summaryQuery = useQuery(
    ["affiliateSummary"],
    async () => {
      const response = await axios.get<AffiliateSummaryResponse>(
        "/accounts/customer/affiliate-earnings/summary",
        {
          params: {
            customerId: activeCustomerOverride,
          },
        }
      );
      return response.data;
    },
    { keepPreviousData: true }
  );

  const conversionsQuery = useQuery(
    [
      "affiliateConversions",
      conversionSortBy,
      conversionSortOrder,
      conversionOffset,
      conversionSearch,
      conversionStatus,
      activeCustomerOverride,
    ],
    async () => {
      const response = await axios.get<ConversionListResponse>(
        "/accounts/customer/affiliate-earnings/conversions",
        {
          params: {
            limit: LIMIT,
            offset: conversionOffset,
            search: conversionSearch || undefined,
            status: conversionStatus || undefined,
            sortBy: conversionSortBy,
            sortOrder: conversionSortOrder,
            customerId: activeCustomerOverride,
          },
        }
      );
      return response.data;
    },
    { keepPreviousData: true }
  );

  const linksQuery = useQuery(
    [
      "affiliateLinks",
      linkSortBy,
      linkSortOrder,
      linkOffset,
      linkSearch,
      activeCustomerOverride,
    ],
    async () => {
      const response = await axios.get<LinkListResponse>(
        "/accounts/customer/affiliate-earnings/links",
        {
          params: {
            limit: LIMIT,
            offset: linkOffset,
            search: linkSearch || undefined,
            sortBy: linkSortBy,
            sortOrder: linkSortOrder,
            customerId: activeCustomerOverride,
          },
        }
      );
      return response.data;
    },
    { keepPreviousData: true }
  );

  const payoutStatusQuery = useQuery(
    ["affiliatePayoutStatus", activeCustomerOverride],
    async () => {
      const response = await axios.get<AffiliatePayoutStatusResponse>(
        "/accounts/customer/affiliate-earnings/payout-status",
        {
          params: {
            customerId: activeCustomerOverride,
          },
        }
      );
      return response.data;
    },
    { keepPreviousData: true }
  );

  const payoutLedgerQuery = useQuery(
    ["affiliatePayoutLedger", activeCustomerOverride],
    async () => {
      const response = await axios.get<AffiliatePayoutLedgerListResponse>(
        "/accounts/customer/affiliate-earnings/payout-ledger",
        {
          params: {
            limit: 10,
            sortBy: "periodEnd",
            sortOrder: "desc",
            customerId: activeCustomerOverride,
          },
        }
      );
      return response.data;
    },
    { keepPreviousData: true }
  );

  const customerOptionsQuery = useQuery(
    ["affiliateAdminCustomers", canViewAffiliateAdminDashboard],
    async () => {
      const response = await axios.get<CustomerOptionsResponse>(
        "/accounts/products/unified/analytics/customers"
      );
      return response.data;
    },
    { enabled: canViewAffiliateAdminDashboard }
  );

  const adminOverviewQuery = useQuery(
    [
      "affiliateAdminOverview",
      canViewAffiliateAdminDashboard,
      adminSearch,
      adminSortBy,
      adminSortOrder,
      adminOffset,
    ],
    async () => {
      const response = await axios.get<AdminAffiliateOverviewResponse>(
        "/accounts/customer/affiliate-earnings/admin/overview",
        {
          params: {
            limit: LIMIT,
            offset: adminOffset,
            search: adminSearch || undefined,
            sortBy: adminSortBy,
            sortOrder: adminSortOrder,
          },
        }
      );
      return response.data;
    },
    {
      enabled: canViewAffiliateAdminDashboard,
      keepPreviousData: true,
    }
  );

  const unmatchedActionsQuery = useQuery(
    [
      "affiliateAdminUnmatchedActions",
      canViewAffiliateAdminDashboard,
      unmatchedSearch,
      unmatchedOffset,
    ],
    async () => {
      const response =
        await axios.get<AdminAffiliateUnmatchedActionListResponse>(
          "/accounts/customer/affiliate-earnings/admin/unmatched-actions",
          {
            params: {
              limit: LIMIT,
              offset: unmatchedOffset,
              search: unmatchedSearch || undefined,
            },
          }
        );
      return response.data;
    },
    {
      enabled: canViewAffiliateAdminDashboard,
      keepPreviousData: true,
    }
  );

  const summary = summaryQuery.data;
  const conversionRows = conversionsQuery.data?.rows || [];
  const linkRows = linksQuery.data?.rows || [];
  const payoutStatus = payoutStatusQuery.data;
  const payoutLedgerRows = payoutLedgerQuery.data?.rows || [];
  const unmatchedRows = unmatchedActionsQuery.data?.rows || [];
  const customerOptions = useMemo(
    () => customerOptionsQuery.data?.customers || [],
    [customerOptionsQuery.data?.customers]
  );
  const filteredCustomerOptions = useMemo(() => {
    const needle = customerPickerSearchInput.trim().toLowerCase();
    if (!needle) return customerOptions;
    return customerOptions.filter((customer) => {
      return (
        customer.id.toLowerCase().includes(needle) ||
        (customer.name || "").toLowerCase().includes(needle) ||
        (customer.ownerEmail || "").toLowerCase().includes(needle)
      );
    });
  }, [customerOptions, customerPickerSearchInput]);
  const selectedCustomerOption = useMemo(() => {
    if (!selectedCustomerId) return undefined;
    return customerOptions.find(
      (customer) => customer.id === selectedCustomerId
    );
  }, [customerOptions, selectedCustomerId]);

  const toggleConversionSort = (nextSortBy: string) => {
    if (conversionSortBy === nextSortBy) {
      setConversionSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setConversionSortBy(nextSortBy);
      setConversionSortOrder("desc");
    }
    setConversionOffset(0);
  };

  const toggleLinkSort = (nextSortBy: string) => {
    if (linkSortBy === nextSortBy) {
      setLinkSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setLinkSortBy(nextSortBy);
      setLinkSortOrder("desc");
    }
    setLinkOffset(0);
  };

  const toggleAdminSort = (nextSortBy: string) => {
    if (adminSortBy === nextSortBy) {
      setAdminSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setAdminSortBy(nextSortBy);
      setAdminSortOrder("desc");
    }
    setAdminOffset(0);
  };

  const conversionsPageLabel = useMemo(() => {
    const p = conversionsQuery.data?.pagination;
    if (!p || p.total === 0) return "0 results";
    const start = p.offset + 1;
    const end = Math.min(p.offset + p.limit, p.total);
    return `${start}-${end} of ${p.total}`;
  }, [conversionsQuery.data?.pagination]);

  const linksPageLabel = useMemo(() => {
    const p = linksQuery.data?.pagination;
    if (!p || p.total === 0) return "0 results";
    const start = p.offset + 1;
    const end = Math.min(p.offset + p.limit, p.total);
    return `${start}-${end} of ${p.total}`;
  }, [linksQuery.data?.pagination]);

  const adminPageLabel = useMemo(() => {
    const p = adminOverviewQuery.data?.pagination;
    if (!p || p.total === 0) return "0 results";
    const start = p.offset + 1;
    const end = Math.min(p.offset + p.limit, p.total);
    return `${start}-${end} of ${p.total}`;
  }, [adminOverviewQuery.data?.pagination]);

  const unmatchedPageLabel = useMemo(() => {
    const p = unmatchedActionsQuery.data?.pagination;
    if (!p || p.total === 0) return "0 results";
    const start = p.offset + 1;
    const end = Math.min(p.offset + p.limit, p.total);
    return `${start}-${end} of ${p.total}`;
  }, [unmatchedActionsQuery.data?.pagination]);

  const openAffiliatePayoutLink = async (mode: "onboarding" | "dashboard") => {
    try {
      setPayoutCtaLoading(mode);
      const endpoint =
        mode === "onboarding"
          ? "/accounts/customer/affiliate-earnings/payout-onboarding-link"
          : "/accounts/customer/affiliate-earnings/payout-dashboard-link";
      const response = await axios.post<AffiliatePayoutLinkResponse>(
        endpoint,
        {},
        {
          params: {
            customerId: activeCustomerOverride,
          },
        }
      );
      const url = response.data?.url;
      if (!url) {
        throw new Error("Missing redirect URL");
      }
      window.open(url, "_blank", "noopener,noreferrer");
      await payoutStatusQuery.refetch();
    } catch (err: any) {
      error(
        err?.response?.data?.cause ||
          "Could not open Stripe payout setup. Please try again."
      );
    } finally {
      setPayoutCtaLoading(null);
    }
  };

  const runAdminSettlement = async (dryRun: boolean) => {
    try {
      setAdminSettlementLoading(dryRun ? "dryRun" : "run");
      const response = await axios.post<AdminSettlementResponse>(
        "/accounts/customer/affiliate-earnings/admin/payout-settlement",
        {
          dryRun,
          confirm: !dryRun,
          customerId: selectedCustomerId || undefined,
        }
      );
      setAdminSettlementResult(response.data);
      payoutLedgerQuery.refetch();
      payoutStatusQuery.refetch();
      summaryQuery.refetch();
      conversionsQuery.refetch();
      linksQuery.refetch();
      adminOverviewQuery.refetch();
    } catch (err: any) {
      error(
        err?.response?.data?.cause ||
          "Could not run affiliate payout settlement."
      );
    } finally {
      setAdminSettlementLoading(null);
    }
  };

  const reconcileUnmatchedAction = async () => {
    if (!reconcileActionId.trim()) {
      error("Impact action ID is required.");
      return;
    }
    if (!reconcileAffiliateLinkId.trim()) {
      error("Affiliate link ID is required.");
      return;
    }
    try {
      setReconcileLoading(true);
      await axios.post(
        "/accounts/customer/affiliate-earnings/admin/reconcile-unmatched-action",
        {
          impactActionId: reconcileActionId.trim(),
          affiliateLinkId: reconcileAffiliateLinkId.trim(),
        }
      );
      setReconcileActionId("");
      unmatchedActionsQuery.refetch();
      summaryQuery.refetch();
      conversionsQuery.refetch();
      linksQuery.refetch();
      adminOverviewQuery.refetch();
      payoutLedgerQuery.refetch();
    } catch (err: any) {
      error(
        err?.response?.data?.cause ||
          "Could not reconcile unmatched affiliate action."
      );
    } finally {
      setReconcileLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {canViewAffiliateAdminDashboard && (
        <Card className="sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Affiliate Workspace
              </h3>
              <p className="text-sm text-gray-600">
                Switch between your customer-facing earnings view and the Passio
                admin dashboard.
              </p>
            </div>
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
              <button
                type="button"
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  affiliateViewMode === "customer"
                    ? "bg-white text-indigo-700 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => setAffiliateViewMode("customer")}
              >
                Customer View
              </button>
              <button
                type="button"
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  affiliateViewMode === "admin"
                    ? "bg-white text-indigo-700 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => setAffiliateViewMode("admin")}
              >
                Admin View
              </button>
            </div>
          </div>
        </Card>
      )}

      {canViewAffiliateAdminDashboard && affiliateViewMode === "admin" && (
        <Card className="sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Admin Affiliate Dashboard
              </h3>
              <p className="text-sm text-gray-500">
                Passio admin-only view across all customers with override
                controls.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
                <input
                  value={customerPickerSearchInput}
                  onChange={(e) => setCustomerPickerSearchInput(e.target.value)}
                  placeholder="Search customer picker..."
                  className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md min-w-[260px]"
                />
              </div>
              <select
                value={selectedCustomerId}
                onChange={(e) => {
                  setSelectedCustomerId(e.target.value);
                  setConversionOffset(0);
                  setLinkOffset(0);
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md min-w-[260px]"
              >
                <option value="">View my customer context</option>
                {filteredCustomerOptions.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name || "Unnamed Customer"} -{" "}
                    {customer.ownerEmail || "No owner email"} - {customer.id}
                  </option>
                ))}
              </select>
              {selectedCustomerId && (
                <button
                  type="button"
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                  onClick={() => setSelectedCustomerId("")}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="font-medium text-indigo-900">
                  Payout Settlement Controls
                </h4>
                <p className="text-sm text-indigo-700">
                  Admin-only preview and manual execution of Stripe Connect
                  payout settlement.{" "}
                  {selectedCustomerId
                    ? "Runs only for selected customer."
                    : "Runs for all mapped customers."}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                  onClick={() => runAdminSettlement(true)}
                  disabled={adminSettlementLoading !== null}
                >
                  {adminSettlementLoading === "dryRun"
                    ? "Previewing..."
                    : "Preview payout batch"}
                </button>
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                  onClick={() => runAdminSettlement(false)}
                  disabled={adminSettlementLoading !== null}
                >
                  {adminSettlementLoading === "run"
                    ? "Submitting..."
                    : "Run payout settlement now"}
                </button>
              </div>
            </div>
            {adminSettlementResult && (
              <div className="mt-3 rounded-md border border-indigo-200 bg-white p-3">
                <p className="text-sm text-gray-700">
                  Last run: {fmtDate(adminSettlementResult.processedAt)} (
                  {adminSettlementResult.dryRun ? "preview" : "live"})
                </p>
                <div className="mt-2 max-h-[220px] overflow-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-1.5 px-2 text-left">Customer</th>
                        <th className="py-1.5 px-2 text-left">Currency</th>
                        <th className="py-1.5 px-2 text-right">Payout</th>
                        <th className="py-1.5 px-2 text-right">Count</th>
                        <th className="py-1.5 px-2 text-left">Status</th>
                        <th className="py-1.5 px-2 text-left">Transfer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminSettlementResult.customers.flatMap((customer) =>
                        (customer.rows || []).map((row, index) => (
                          <tr
                            key={`${customer.customerId}-${row.currency}-${index}`}
                            className="border-b border-gray-100"
                          >
                            <td className="py-1.5 px-2">
                              {customer.customerId}
                            </td>
                            <td className="py-1.5 px-2">
                              {row.currency || "-"}
                            </td>
                            <td className="py-1.5 px-2 text-right">
                              {fmtMoney(
                                row.grossPayoutAmount || 0,
                                row.currency || "USD"
                              )}
                            </td>
                            <td className="py-1.5 px-2 text-right">
                              {row.conversionCount || 0}
                            </td>
                            <td className="py-1.5 px-2">
                              {toTitle(row.status)}
                              {row.reason ? `: ${row.reason}` : ""}
                            </td>
                            <td className="py-1.5 px-2">
                              {row.stripeTransferId || "-"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="font-medium text-amber-900">
                  Reconcile Unmatched Conversion
                </h4>
                <p className="text-sm text-amber-800">
                  Fix a conversion that exists in Impact but landed as unmatched
                  by linking it to a known affiliate link.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={reconcileActionId}
                  onChange={(e) => setReconcileActionId(e.target.value)}
                  placeholder="Impact action ID"
                  className="px-3 py-2 text-sm border border-amber-300 rounded-md min-w-[260px] bg-white"
                />
                <input
                  value={reconcileAffiliateLinkId}
                  onChange={(e) => setReconcileAffiliateLinkId(e.target.value)}
                  placeholder="Affiliate link ID"
                  className="px-3 py-2 text-sm border border-amber-300 rounded-md min-w-[260px] bg-white"
                />
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                  onClick={reconcileUnmatchedAction}
                  disabled={reconcileLoading}
                >
                  {reconcileLoading ? "Reconciling..." : "Reconcile now"}
                </button>
              </div>
            </div>
            <div className="mt-3 border border-amber-200 rounded-lg overflow-hidden bg-white">
              <div className="p-3 border-b border-amber-200 bg-amber-50 flex items-center justify-between gap-2">
                <h5 className="font-medium text-amber-900">
                  Unmatched Impact Actions
                </h5>
                <div className="relative">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
                  <input
                    value={unmatchedSearchInput}
                    onChange={(e) => setUnmatchedSearchInput(e.target.value)}
                    placeholder="Search action ID, reason, payload..."
                    className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md w-[320px] bg-white"
                  />
                </div>
              </div>
              <div className="overflow-auto max-h-[260px]">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr className="border-b border-gray-200">
                      <th className="py-2 px-3 text-left">Action ID</th>
                      <th className="py-2 px-3 text-left">Status</th>
                      <th className="py-2 px-3 text-right">Sale</th>
                      <th className="py-2 px-3 text-right">Payout</th>
                      <th className="py-2 px-3 text-left">Reason</th>
                      <th className="py-2 px-3 text-left">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedActionsQuery.isLoading ? (
                      <tr>
                        <td colSpan={6} className="py-6 text-center">
                          <Spinner className="!w-[1.25rem] mx-auto" />
                        </td>
                      </tr>
                    ) : unmatchedRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="py-6 text-center text-gray-500"
                        >
                          No unmatched actions found.
                        </td>
                      </tr>
                    ) : (
                      unmatchedRows.map((row) => (
                        <tr
                          key={row.impactActionId}
                          className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                          onClick={() =>
                            setReconcileActionId(row.impactActionId)
                          }
                        >
                          <td className="py-2 px-3 font-medium text-indigo-700">
                            {row.impactActionId}
                          </td>
                          <td className="py-2 px-3">{toTitle(row.status)}</td>
                          <td className="py-2 px-3 text-right">
                            {fmtMoney(
                              row.saleAmount || 0,
                              row.currency || "USD"
                            )}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {fmtMoney(
                              row.payoutAmount || 0,
                              row.currency || "USD"
                            )}
                          </td>
                          <td className="py-2 px-3 text-gray-700">
                            {row.mismatchReason || "-"}
                          </td>
                          <td className="py-2 px-3">{fmtDate(row.updated)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm">
                <span className="text-gray-600">{unmatchedPageLabel}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                    disabled={unmatchedOffset === 0}
                    onClick={() =>
                      setUnmatchedOffset((prev) => Math.max(0, prev - LIMIT))
                    }
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                    disabled={!unmatchedActionsQuery.data?.pagination.hasMore}
                    onClick={() => setUnmatchedOffset((prev) => prev + LIMIT)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
              <h4 className="font-medium text-gray-900">
                All Customers Overview
              </h4>
              <div className="relative">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
                <input
                  value={adminSearchInput}
                  onChange={(e) => setAdminSearchInput(e.target.value)}
                  placeholder="Search customer, email, id..."
                  className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md w-[280px] bg-white"
                />
              </div>
            </div>
            <div className="overflow-auto max-h-[360px]">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleAdminSort("customerName")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Customer
                        {adminSortBy === "customerName" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleAdminSort("ownerEmail")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Owner
                        {adminSortBy === "ownerEmail" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleAdminSort("trackedLinks")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Links
                        {adminSortBy === "trackedLinks" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleAdminSort("clicks")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Clicks
                        {adminSortBy === "clicks" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleAdminSort("conversions")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Conversions
                        {adminSortBy === "conversions" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleAdminSort("approvedConversions")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Approved
                        {adminSortBy === "approvedConversions" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleAdminSort("totalPayoutAmount")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Payout
                        {adminSortBy === "totalPayoutAmount" &&
                          (adminSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {adminOverviewQuery.isLoading ? (
                    <tr>
                      <td colSpan={7} className="py-6 text-center">
                        <Spinner className="!w-[1.25rem] mx-auto" />
                      </td>
                    </tr>
                  ) : (adminOverviewQuery.data?.rows || []).length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="py-6 text-center text-gray-500"
                      >
                        No customers found.
                      </td>
                    </tr>
                  ) : (
                    (adminOverviewQuery.data?.rows || []).map((row) => (
                      <tr
                        key={row.customerId}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-2 px-3">
                          <button
                            type="button"
                            className="text-indigo-700 hover:underline"
                            onClick={() => {
                              setSelectedCustomerId(row.customerId);
                              setAffiliateViewMode("customer");
                            }}
                          >
                            {row.customerName || row.customerId}
                          </button>
                        </td>
                        <td className="py-2 px-3 text-gray-600">
                          {row.ownerEmail || "-"}
                        </td>
                        <td className="py-2 px-3 text-right">
                          {row.trackedLinks}
                        </td>
                        <td className="py-2 px-3 text-right">{row.clicks}</td>
                        <td className="py-2 px-3 text-right">
                          {row.conversions}
                        </td>
                        <td className="py-2 px-3 text-right">
                          {row.approvedConversions}
                        </td>
                        <td className="py-2 px-3 text-right text-green-700">
                          {fmtMoney(row.totalPayoutAmount || 0)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm">
              <span className="text-gray-600">{adminPageLabel}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                  disabled={adminOffset === 0}
                  onClick={() =>
                    setAdminOffset((prev) => Math.max(0, prev - LIMIT))
                  }
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                  disabled={!adminOverviewQuery.data?.pagination.hasMore}
                  onClick={() => setAdminOffset((prev) => prev + LIMIT)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {(affiliateViewMode === "customer" || !canViewAffiliateAdminDashboard) &&
        selectedCustomerId && (
          <Card className="sm:p-4 border border-indigo-200 bg-indigo-50">
            <p className="text-sm text-indigo-800">
              Viewing affiliate data for customer{" "}
              <span className="font-semibold">
                {selectedCustomerOption?.name || "Unnamed Customer"}
              </span>{" "}
              ({selectedCustomerId}).
            </p>
          </Card>
        )}

      {(affiliateViewMode === "customer" ||
        !canViewAffiliateAdminDashboard) && (
        <>
          <div className="grid gap-4 lg:grid-cols-4">
            <Card className="p-4 lg:col-span-1">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-gray-900">
                  Payout Setup
                </h3>
                {payoutStatusQuery.isLoading ? (
                  <Spinner className="!w-[1rem]" />
                ) : payoutStatus?.readyForPayouts ? (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                    Ready
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                    Setup required
                  </span>
                )}
              </div>

              {!payoutStatusQuery.isLoading &&
                (() => {
                  const friendlyRequirements = dedupeFriendlyStripeRequirements(
                    payoutStatus?.requirementsCurrentlyDue
                  );
                  const pendingFriendlyCount = dedupeFriendlyStripeRequirements(
                    payoutStatus?.requirementsPendingVerification
                  ).length;
                  const hasConnect = !!payoutStatus?.hasConnectAccount;
                  const detailsSubmitted = !!payoutStatus?.detailsSubmitted;
                  const requirementsCount = friendlyRequirements.length;

                  let primaryCtaLabel = "Set up payouts";
                  if (payoutCtaLoading === "onboarding") {
                    primaryCtaLabel = "Opening Stripe...";
                  } else if (!hasConnect) {
                    primaryCtaLabel = "Set up payouts";
                  } else if (!detailsSubmitted) {
                    primaryCtaLabel = "Continue setup";
                  } else if (requirementsCount > 0) {
                    primaryCtaLabel = "Continue verification";
                  } else if (payoutStatus?.readyForPayouts) {
                    primaryCtaLabel = "Manage payouts";
                  } else {
                    primaryCtaLabel = "Continue setup";
                  }

                  return (
                    <>
                      <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-gray-500">
                          Pending balance
                        </p>
                        <p className="mt-0.5 text-xl font-semibold text-gray-900">
                          {fmtStripeMinorMoney(
                            payoutStatus?.pendingPayoutAmount || 0,
                            payoutStatus?.pendingPayoutCurrency || "USD"
                          )}
                        </p>
                        {payoutStatus?.lastPayoutAt ? (
                          <p className="text-[11px] text-gray-500 mt-1">
                            Last payout{" "}
                            {fmtStripeMinorMoney(
                              payoutStatus?.lastPayoutAmount,
                              payoutStatus?.lastPayoutCurrency || "USD"
                            )}{" "}
                            on{" "}
                            {fmtDate(
                              new Date(
                                payoutStatus.lastPayoutAt * 1000
                              ).toISOString()
                            )}
                          </p>
                        ) : (
                          <p className="text-[11px] text-gray-500 mt-1">
                            No payouts yet
                          </p>
                        )}
                      </div>

                      {requirementsCount > 0 && (
                        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                          <p className="font-medium">
                            Stripe still needs {requirementsCount} more{" "}
                            {requirementsCount === 1 ? "thing" : "things"} to
                            enable payouts:
                          </p>
                          <ul className="mt-1.5 space-y-1">
                            {friendlyRequirements.slice(0, 5).map((label) => (
                              <li
                                key={label}
                                className="flex items-start gap-2"
                              >
                                <span className="mt-1.5 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-600" />
                                <span>{label}</span>
                              </li>
                            ))}
                            {requirementsCount > 5 && (
                              <li className="pl-3.5 text-[11px] text-amber-800">
                                + {requirementsCount - 5} more (Stripe will show
                                the full list)
                              </li>
                            )}
                          </ul>
                        </div>
                      )}

                      {requirementsCount === 0 &&
                        pendingFriendlyCount > 0 &&
                        !payoutStatus?.readyForPayouts && (
                          <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                            <p className="font-medium">
                              Stripe is verifying your information.
                            </p>
                            <p className="mt-0.5">
                              This usually takes 1&ndash;2 business days. We
                              start payouts as soon as it&apos;s approved.
                            </p>
                          </div>
                        )}

                      <div className="mt-3 flex flex-col gap-2">
                        <button
                          type="button"
                          className="inline-flex justify-center items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                          onClick={() => openAffiliatePayoutLink("onboarding")}
                          disabled={payoutCtaLoading !== null}
                        >
                          {primaryCtaLabel}
                        </button>
                        {detailsSubmitted && (
                          <button
                            type="button"
                            className="inline-flex justify-center items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                            onClick={() => openAffiliatePayoutLink("dashboard")}
                            disabled={payoutCtaLoading !== null}
                          >
                            {payoutCtaLoading === "dashboard"
                              ? "Opening dashboard..."
                              : "Open Stripe dashboard"}
                          </button>
                        )}
                      </div>
                    </>
                  );
                })()}
            </Card>

            <Card className="p-0 overflow-hidden lg:col-span-3">
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Payout Ledger
                  </h4>
                  {payoutLedgerQuery.isFetching && (
                    <span className="text-sm text-gray-500">Refreshing...</span>
                  )}
                </div>
              </div>
              <div className="overflow-auto max-h-[360px]">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr className="border-b border-gray-200">
                      <th className="py-2 px-3 text-left">Period</th>
                      <th className="py-2 px-3 text-right">Net Payout</th>
                      <th className="py-2 px-3 text-right">Conversions</th>
                      <th className="py-2 px-3 text-left">Status</th>
                      <th className="py-2 px-3 text-left">Stripe Payout</th>
                      <th className="py-2 px-3 text-left">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payoutLedgerQuery.isLoading ? (
                      <tr>
                        <td colSpan={6} className="py-6 text-center">
                          <Spinner className="!w-[1.25rem] mx-auto" />
                        </td>
                      </tr>
                    ) : payoutLedgerRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="py-6 text-center text-gray-500"
                        >
                          No payout ledger entries yet.
                        </td>
                      </tr>
                    ) : (
                      payoutLedgerRows.map((row) => (
                        <tr
                          key={row.affiliatePayoutId}
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          <td className="py-2 px-3">
                            {fmtDate(row.periodStart)} -{" "}
                            {fmtDate(row.periodEnd)}
                          </td>
                          <td className="py-2 px-3 text-right text-green-700">
                            {fmtMoney(row.netPayoutAmount, row.payoutCurrency)}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {row.sourceConversionCount}
                          </td>
                          <td className="py-2 px-3">
                            {toTitle(row.payoutStatus)}
                          </td>
                          <td className="py-2 px-3">
                            {row.stripePayoutId || "-"}
                          </td>
                          <td className="py-2 px-3">{fmtDate(row.updated)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          <Card className="sm:p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Affiliate Earnings
                </h3>
                <p className="text-sm text-gray-500">
                  Track generated Instacart affiliate links, conversion actions,
                  and earnings attributed to this customer.
                </p>
              </div>
              {(summaryQuery.isFetching ||
                conversionsQuery.isFetching ||
                linksQuery.isFetching) && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Refreshing
                </div>
              )}
            </div>

            {summaryQuery.isLoading ? (
              <div className="py-10 flex justify-center">
                <Spinner className="!w-[2rem]" />
              </div>
            ) : (
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                <Card className="p-4 border border-gray-200">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Tracked Links
                  </p>
                  <p className="mt-1 text-2xl font-semibold">
                    {summary?.trackedLinks ?? 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Last link: {fmtDate(summary?.lastTrackedLinkAt)}
                  </p>
                </Card>
                <Card className="p-4 border border-gray-200">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Clicks
                  </p>
                  <p className="mt-1 text-2xl font-semibold">
                    {summary?.clicks ?? 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Last click: {fmtDate(summary?.lastClickAt)}
                  </p>
                </Card>
                <Card className="p-4 border border-gray-200">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Conversions
                  </p>
                  <p className="mt-1 text-2xl font-semibold">
                    {summary?.conversions ?? 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Last conversion: {fmtDate(summary?.lastConversionAt)}
                  </p>
                </Card>
                <Card className="p-4 border border-gray-200">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Total Sales
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-indigo-700">
                    {fmtMoney(summary?.totalSaleAmount || 0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Approved: {summary?.approvedConversions ?? 0} | Pending:{" "}
                    {summary?.pendingConversions ?? 0}
                  </p>
                </Card>
                <Card className="p-4 border border-gray-200">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Total Payout (All Statuses)
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-green-700">
                    {fmtMoney(summary?.totalPayoutAmount || 0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Reversed: {summary?.reversedConversions ?? 0}
                  </p>
                </Card>
                <Card className="p-4 border border-green-200 bg-green-50">
                  <p className="text-xs uppercase tracking-wide text-green-700">
                    Payout Eligible (Approved + Locked)
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-green-800">
                    {fmtMoney(summary?.eligiblePayoutAmount || 0)}
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Eligible conversions: {summary?.eligibleConversions ?? 0}
                  </p>
                </Card>
              </div>
            )}

            {summary && (
              <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
                <div className="grid gap-3 md:grid-cols-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Sources
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {summary.topSourceTools.length === 0 ? (
                        <span className="text-xs text-gray-500">
                          No data yet
                        </span>
                      ) : (
                        summary.topSourceTools.map((item) => {
                          const isActive =
                            activeAttributionFilter?.type === "source" &&
                            activeAttributionFilter.value === item.key;
                          return (
                            <button
                              key={`${item.key}-${item.count}`}
                              type="button"
                              onClick={() =>
                                isActive
                                  ? clearAttributionFilter()
                                  : applyAttributionFilter("source", item.key)
                              }
                              title={
                                isActive
                                  ? "Clear filter"
                                  : `Filter by source: ${item.key}`
                              }
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition ${
                                isActive
                                  ? "bg-indigo-600 text-white"
                                  : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                              }`}
                            >
                              {toTitle(item.key)}
                              <span
                                className={`text-[10px] ${
                                  isActive
                                    ? "text-indigo-100"
                                    : "text-indigo-500"
                                }`}
                              >
                                {item.count}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Campaigns
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {summary.topCampaigns.length === 0 ? (
                        <span className="text-xs text-gray-500">
                          No data yet
                        </span>
                      ) : (
                        summary.topCampaigns.map((item) => {
                          const isActive =
                            activeAttributionFilter?.type === "campaign" &&
                            activeAttributionFilter.value === item.key;
                          return (
                            <button
                              key={`${item.key}-${item.count}`}
                              type="button"
                              onClick={() =>
                                isActive
                                  ? clearAttributionFilter()
                                  : applyAttributionFilter("campaign", item.key)
                              }
                              title={
                                isActive
                                  ? "Clear filter"
                                  : `Filter by campaign: ${item.key}`
                              }
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition ${
                                isActive
                                  ? "bg-violet-600 text-white"
                                  : "bg-violet-100 text-violet-700 hover:bg-violet-200"
                              }`}
                            >
                              {toTitle(item.key)}
                              <span
                                className={`text-[10px] ${
                                  isActive
                                    ? "text-violet-100"
                                    : "text-violet-500"
                                }`}
                              >
                                {item.count}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Currencies
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {summary.topCurrencies.length === 0 ? (
                        <span className="text-xs text-gray-500">
                          No data yet
                        </span>
                      ) : (
                        summary.topCurrencies.map((item) => (
                          <span
                            key={`${item.currency}-${item.count}`}
                            className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700"
                          >
                            {item.currency}
                            <span className="text-[10px] text-gray-500">
                              {item.count}
                            </span>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>

          <Card className="sm:p-4 border border-indigo-100 bg-indigo-50/30">
            <button
              type="button"
              onClick={() => setDocsExpanded((prev) => !prev)}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <div className="flex items-center gap-2.5">
                <div className="rounded-md bg-indigo-100 p-1.5 text-indigo-700">
                  <CodeBracketIcon className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    How to Generate Affiliate Links
                  </h3>
                  <p className="text-xs text-gray-600">
                    N-API endpoint, optional params, and a live request builder.
                    Every link is tracked here automatically.
                  </p>
                </div>
              </div>
              <div className="text-gray-500">
                {docsExpanded ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
              </div>
            </button>

            {docsExpanded && (
              <div className="mt-3 space-y-3">
                <div className="rounded-md border border-indigo-200 bg-white p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                      POST
                    </span>
                    <code className="flex-1 break-all rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[12px] text-gray-800">
                      {passioApiBaseUrl()}
                      {AFFILIATE_LINK_NAPI_PATH}
                    </code>
                    <span className="text-[11px] text-gray-600">
                      Auth:{" "}
                      <code className="rounded bg-gray-100 px-1">
                        Bearer &lt;napi-token&gt;
                      </code>
                    </span>
                  </div>
                  <p className="mt-2 text-[11px] text-gray-600">
                    Body is a JSON array of shopping items{" "}
                    <code>{`{ name, portionQuantity, portionSize }`}</code> (or
                    a full <code>LLMMealPlanResultOutgoing</code> with{" "}
                    <code>shoppingList</code> / <code>mealPlanDays</code>).
                    Response: <code>passio_redirect_url</code> — always send
                    users through that URL.
                  </p>
                </div>

                <div className="rounded-md border border-indigo-200 bg-white p-3">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                      Optional params · live builder
                    </h4>
                    <span className="text-[10px] text-gray-500">
                      All optional. Leave blank to omit.
                    </span>
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-3">
                    <label className="text-[11px] font-medium text-gray-700">
                      sourceTool
                      <input
                        value={builderSourceTool}
                        onChange={(e) => setBuilderSourceTool(e.target.value)}
                        placeholder="e.g. mealplanner-v2"
                        className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-xs font-mono"
                      />
                      <span className="text-[10px] text-gray-500">
                        → Source column
                      </span>
                    </label>
                    <label className="text-[11px] font-medium text-gray-700">
                      cartId
                      <input
                        value={builderCartId}
                        onChange={(e) => setBuilderCartId(e.target.value)}
                        placeholder="e.g. cart-abc-123"
                        className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-xs font-mono"
                      />
                      <span className="text-[10px] text-gray-500">
                        → Cart column
                      </span>
                    </label>
                    <label className="text-[11px] font-medium text-gray-700">
                      campaign
                      <input
                        value={builderCampaign}
                        onChange={(e) => setBuilderCampaign(e.target.value)}
                        placeholder="e.g. spring-launch"
                        className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-xs font-mono"
                      />
                      <span className="text-[10px] text-gray-500">
                        → Campaign column
                      </span>
                    </label>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Request URL
                    </p>
                    <CopyButton value={builderUrl} label="URL" />
                  </div>
                  <code className="mt-1 block break-all rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-800">
                    {builderUrl}
                  </code>

                  <div className="mt-2 flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Example curl
                    </p>
                    <CopyButton value={builderCurl} label="curl" />
                  </div>
                  <pre className="mt-1 overflow-x-auto rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-800">
                    {builderCurl}
                  </pre>
                </div>
              </div>
            )}
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Conversion Actions
                  </h4>
                  {activeAttributionFilter && (
                    <button
                      type="button"
                      onClick={clearAttributionFilter}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        activeAttributionFilter.type === "campaign"
                          ? "bg-violet-600 text-white hover:bg-violet-700"
                          : "bg-indigo-600 text-white hover:bg-indigo-700"
                      }`}
                      title="Clear filter"
                    >
                      Filtered: {activeAttributionFilter.type} ={" "}
                      {activeAttributionFilter.value}
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
                    <input
                      value={conversionSearchInput}
                      onChange={(e) => setConversionSearchInput(e.target.value)}
                      placeholder="Search action ID, campaign, cart..."
                      className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md w-[280px]"
                    />
                  </div>
                  <select
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                    value={conversionStatus}
                    onChange={(e) => {
                      setConversionStatus(e.target.value);
                      setConversionOffset(0);
                    }}
                  >
                    <option value="">All statuses</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="reversed">Reversed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="overflow-auto max-h-[520px]">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="py-2 px-3 text-left">Action ID</th>
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleConversionSort("status")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Status
                        {conversionSortBy === "status" &&
                          (conversionSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleConversionSort("saleAmount")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Sale
                        {conversionSortBy === "saleAmount" &&
                          (conversionSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleConversionSort("payoutAmount")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Payout
                        {conversionSortBy === "payoutAmount" &&
                          (conversionSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th className="py-2 px-3 text-left">Source</th>
                    <th className="py-2 px-3 text-left">Campaign</th>
                    <th className="py-2 px-3 text-left">Cart</th>
                    <th className="py-2 px-3 text-left">Payout Gate</th>
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleConversionSort("actionOccurredAt")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Occurred
                        {conversionSortBy === "actionOccurredAt" &&
                          (conversionSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {conversionsQuery.isLoading ? (
                    <tr>
                      <td colSpan={9} className="py-8 text-center">
                        <Spinner className="!w-[1.5rem] mx-auto" />
                      </td>
                    </tr>
                  ) : conversionRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="py-8 text-center text-gray-500"
                      >
                        No conversion actions found.
                      </td>
                    </tr>
                  ) : (
                    conversionRows.map((row) => (
                      <tr
                        key={row.impactActionId}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-2 px-3 font-medium text-gray-800">
                          {row.impactActionId}
                        </td>
                        <td className="py-2 px-3">
                          <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700">
                            {toTitle(row.status)}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right">
                          {fmtMoney(row.saleAmount, row.currency || "USD")}
                        </td>
                        <td className="py-2 px-3 text-right text-green-700">
                          {fmtMoney(row.payoutAmount, row.currency || "USD")}
                        </td>
                        <td className="py-2 px-3">{toTitle(row.sourceTool)}</td>
                        <td className="py-2 px-3">{row.campaign || "-"}</td>
                        <td className="py-2 px-3">{row.cartId || "-"}</td>
                        <td className="py-2 px-3">
                          {row.isPayoutEligible ? (
                            <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-[11px] text-green-700">
                              Eligible
                            </span>
                          ) : (
                            <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-700">
                              Waiting
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          {fmtDate(row.actionOccurredAt)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm">
              <span className="text-gray-600">{conversionsPageLabel}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                  disabled={conversionOffset === 0}
                  onClick={() =>
                    setConversionOffset((prev) => Math.max(0, prev - LIMIT))
                  }
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                  disabled={!conversionsQuery.data?.pagination.hasMore}
                  onClick={() => setConversionOffset((prev) => prev + LIMIT)}
                >
                  Next
                </button>
              </div>
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Tracked Affiliate Links
                  </h4>
                  {activeAttributionFilter && (
                    <button
                      type="button"
                      onClick={clearAttributionFilter}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        activeAttributionFilter.type === "campaign"
                          ? "bg-violet-600 text-white hover:bg-violet-700"
                          : "bg-indigo-600 text-white hover:bg-indigo-700"
                      }`}
                      title="Clear filter"
                    >
                      Filtered: {activeAttributionFilter.type} ={" "}
                      {activeAttributionFilter.value}
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="relative">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
                  <input
                    value={linkSearchInput}
                    onChange={(e) => setLinkSearchInput(e.target.value)}
                    placeholder="Search source, campaign, cart, subId..."
                    className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md w-[280px]"
                  />
                </div>
              </div>
            </div>
            <div className="overflow-auto max-h-[520px]">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="py-2 px-3 text-left">Sub ID 1</th>
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleLinkSort("sourceTool")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Source
                        {linkSortBy === "sourceTool" &&
                          (linkSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleLinkSort("campaign")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Campaign
                        {linkSortBy === "campaign" &&
                          (linkSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th className="py-2 px-3 text-left">Cart</th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleLinkSort("clickCount")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Clicks
                        {linkSortBy === "clickCount" &&
                          (linkSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleLinkSort("conversionCount")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Conversions
                        {linkSortBy === "conversionCount" &&
                          (linkSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-right cursor-pointer"
                      onClick={() => toggleLinkSort("totalPayoutAmount")}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        Payout
                        {linkSortBy === "totalPayoutAmount" &&
                          (linkSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="py-2 px-3 text-left cursor-pointer"
                      onClick={() => toggleLinkSort("created")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Created
                        {linkSortBy === "created" &&
                          (linkSortOrder === "asc" ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th className="py-2 px-3 text-right">Redirect URL</th>
                  </tr>
                </thead>
                <tbody>
                  {linksQuery.isLoading ? (
                    <tr>
                      <td colSpan={9} className="py-8 text-center">
                        <Spinner className="!w-[1.5rem] mx-auto" />
                      </td>
                    </tr>
                  ) : linkRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="py-8 text-center text-gray-500"
                      >
                        No tracked links found.
                      </td>
                    </tr>
                  ) : (
                    linkRows.map((row) => {
                      const redirectUrl = `${passioApiBaseUrl()}/v2/products/affiliates/redirect/${
                        row.affiliateLinkId
                      }`;
                      return (
                        <tr
                          key={row.affiliateLinkId}
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          <td className="py-2 px-3 font-medium text-gray-800">
                            {row.impactSubId1 || row.affiliateLinkId}
                          </td>
                          <td className="py-2 px-3">
                            {toTitle(row.sourceTool)}
                          </td>
                          <td className="py-2 px-3">{row.campaign || "-"}</td>
                          <td className="py-2 px-3">{row.cartId || "-"}</td>
                          <td className="py-2 px-3 text-right">
                            {row.clickCount}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {row.conversionCount}
                          </td>
                          <td className="py-2 px-3 text-right text-green-700">
                            {fmtMoney(row.totalPayoutAmount)}
                          </td>
                          <td className="py-2 px-3">{fmtDate(row.created)}</td>
                          <td className="py-2 px-3 text-right">
                            <CopyButton
                              value={redirectUrl}
                              label="Copy"
                              iconOnly
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm">
              <span className="text-gray-600">{linksPageLabel}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                  disabled={linkOffset === 0}
                  onClick={() =>
                    setLinkOffset((prev) => Math.max(0, prev - LIMIT))
                  }
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
                  disabled={!linksQuery.data?.pagination.hasMore}
                  onClick={() => setLinkOffset((prev) => prev + LIMIT)}
                >
                  Next
                </button>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default AffiliateEarnings;
